import React, { useState, useRef, useEffect } from "react";
import JsBarcode from "jsbarcode";
import { Search, ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import api, { LOGO_URL, GOLD_STAR_3D } from "@/lib/api";

export default function CredentialVerification() {
  const [cid, setCid] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (result && result.valid && barcodeRef.current && result.credential_id) {
      try {
        JsBarcode(barcodeRef.current, result.credential_id, {
          format: "CODE128", displayValue: true, fontSize: 12, font: "monospace",
          height: 40, width: 1.6, margin: 6, background: "#ffffff", lineColor: "#0b0b0b",
        });
      } catch (e) { /* ignore */ }
    }
  }, [result]);

  const verify = async (e) => {
    e.preventDefault();
    if (!cid.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.get(`/verify/${encodeURIComponent(cid.trim())}`);
      setResult(res.data);
    } catch (err) {
      setResult({ valid: false, credential_id: cid.trim() });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Institutional Verification"
        title="Authenticate a credential issued by the Vault."
        subtitle="Third parties and aligned institutions can confirm the validity of a digital economic citizenship credential against the decentralized registry."
      />

      <section className="py-20 bg-ink">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal>
            <form onSubmit={verify} data-testid="verify-credential-form" className="border border-gold/20 bg-ink-surface p-8 sm:p-10">
              <Eyebrow>Credential Lookup</Eyebrow>
              <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60 block mt-6">Credential ID</label>
              <div className="flex items-center gap-4 border-b border-gold/30 focus-within:border-gold mt-2 transition-colors">
                <input
                  data-testid="credential-input"
                  value={cid}
                  onChange={(e) => setCid(e.target.value.toUpperCase())}
                  placeholder="MFB-XXXX-XXXX"
                  className="flex-1 bg-transparent outline-none py-3 font-mono text-lg tracking-widest text-gold-text placeholder:text-[#5f5c57]"
                />
                <button type="submit" disabled={busy} data-testid="verify-submit" className="text-gold hover:text-gold-text transition-colors">
                  {busy ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                </button>
              </div>
            </form>
          </Reveal>

          {result && (
            <Reveal>
              <div data-testid="verify-result" className={`mt-8 border p-8 ${result.valid ? "border-gold/40 bg-ink-surface" : "border-red-900/50 bg-red-950/20"}`}>
                {result.valid ? (
                  <>
                    {/* Verified header */}
                    <div className="flex items-start justify-between gap-3 pb-6 border-b border-gold/15">
                      <div className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="7 Generations" className="h-10 w-auto object-contain" />
                        <div>
                          <p className="font-mono text-[8px] tracking-[0.2em] uppercase text-gold">Mississippian Heritage · Xulla Affiliate</p>
                          <p className="font-serif text-lg text-gold-text leading-tight">Multinational Tribal Identification</p>
                        </div>
                      </div>
                      <img src={GOLD_STAR_3D} alt="Compliance star" className="w-10 h-10 object-contain mix-blend-lighten shrink-0" />
                    </div>

                    <div className="flex items-center gap-3 text-gold mt-6">
                      <ShieldCheck size={26} strokeWidth={1.4} />
                      <span className="font-mono text-sm tracking-[0.2em] uppercase">Valid Credential</span>
                    </div>
                    <dl className="mt-6 space-y-4">
                      {[
                        ["Credential ID", result.credential_id],
                        ["Status", result.status],
                        ["Records Anchored", result.records_anchored],
                        ["Lineage Anchor", result.lineage_anchor],
                        ["Issuing Authority", result.issuing_authority],
                      ].map(([k, v]) => (
                        <div key={k} className="flex flex-col sm:flex-row sm:justify-between gap-1 border-b border-gold/10 pb-3">
                          <dt className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a766f]">{k}</dt>
                          <dd className="font-sans text-sm text-[#F5F0EB] sm:text-right break-all">{v}</dd>
                        </div>
                      ))}
                    </dl>

                    {/* Scannable barcode */}
                    <div className="mt-8 flex flex-col items-center">
                      <p className="font-mono text-[9px] tracking-[0.24em] uppercase text-gold/50 mb-3">Scan to confirm credential</p>
                      <div className="bg-white p-2 rounded-[2px]">
                        <svg ref={barcodeRef} data-testid="verify-barcode" className="block" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3 text-red-400">
                    <ShieldX size={26} strokeWidth={1.4} />
                    <div>
                      <p className="font-mono text-sm tracking-[0.2em] uppercase">Not Found</p>
                      <p className="font-sans text-sm text-[#A19D98] mt-1">No active credential matches "{result.credential_id}".</p>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
